// ============================================================================
// «Verificar con Google» — lo que necesita un aprobador de la IA.
// ============================================================================
// El backend sólo acepta como aprobador a staff cuyo correo del dominio
// autorizado lo PROBÓ GOOGLE (la bandera emailVerified de Firebase no cuenta:
// el servidor la ponía sin comprobar nada). Dumax entra con correo y
// contraseña, así que la persona enlaza una vez su cuenta de Google:
//   1. popup de Google sobre la sesión de Firebase ya abierta (linkWithPopup;
//      si Google ya estaba enlazado, reauthenticateWithPopup),
//   2. se manda el ID token de GOOGLE a POST /auth/link-google, que comprueba
//      firma, audiencia y email_verified y deja la constancia en el servidor.
// El enlace hecho sólo en el cliente NO cuenta: sin el paso 2 no hay aprobador.
//
// La PRIMERA verificación retira la contraseña anterior (la pudo poner un
// admin al crear la cuenta; quien la conoce entraría como tú): la persona elige
// aquí su nueva contraseña (`newPassword`), el servidor cierra TODAS sus
// sesiones y responde `reloginRequired` — hay que volver a entrar con la nueva.
// ============================================================================
import {
  GoogleAuthProvider,
  linkWithPopup,
  reauthenticateWithPopup,
  type Auth,
  type UserCredential,
} from "firebase/auth";
import api from "../../../utils/api";
import { unwrap } from "./envelope";

export interface GoogleProofStatus {
  email: string | null;
  proven: boolean;
}

export interface GoogleProofResult {
  email: string;
  passwordRotated?: boolean;
  reloginRequired?: boolean;
}

/** Mínimo del servidor (googleLink.MIN_PASSWORD_LENGTH = recuperación de contraseña). */
export const MIN_NEW_PASSWORD_LENGTH = 6;

export type GoogleProofErrorKey =
  | "noFirebaseSession"
  | "popupClosed"
  | "wrongAccount"
  | "alreadyLinkedElsewhere"
  | "notConfigured"
  | "badPassword"
  | "failed";

export class GoogleProofError extends Error {
  key: GoogleProofErrorKey;
  constructor(key: GoogleProofErrorKey, message?: string) {
    super(message || key);
    this.key = key;
  }
}

const code = (e: unknown) => (e && typeof e === "object" && "code" in e ? String((e as { code: unknown }).code) : "");

/** Popup de Google sobre la sesión abierta; devuelve el ID token DE GOOGLE. */
export async function obtainGoogleIdToken(auth: Auth, email?: string | null): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new GoogleProofError("noFirebaseSession");
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account", ...(email ? { login_hint: email } : {}) });

  let cred: UserCredential | null = null;
  let idToken: string | null | undefined = null;
  try {
    cred = await linkWithPopup(user, provider);
  } catch (e) {
    const c = code(e);
    if (c === "auth/provider-already-linked") {
      try {
        cred = await reauthenticateWithPopup(user, provider);
      } catch (e2) {
        const c2 = code(e2);
        if (c2 === "auth/user-mismatch") throw new GoogleProofError("wrongAccount");
        if (c2 === "auth/popup-closed-by-user" || c2 === "auth/cancelled-popup-request") {
          throw new GoogleProofError("popupClosed");
        }
        throw new GoogleProofError("failed", c2);
      }
    } else if (c === "auth/credential-already-in-use") {
      // Esa cuenta de Google ya es de otro usuario de Firebase: el servidor lo
      // rechazará con 409; se manda igual para que dé el motivo exacto.
      idToken = GoogleAuthProvider.credentialFromError(e as never)?.idToken;
      if (!idToken) throw new GoogleProofError("alreadyLinkedElsewhere");
    } else if (c === "auth/popup-closed-by-user" || c === "auth/cancelled-popup-request") {
      throw new GoogleProofError("popupClosed");
    } else {
      throw new GoogleProofError("failed", c);
    }
  }
  if (!idToken && cred) idToken = GoogleAuthProvider.credentialFromResult(cred)?.idToken;
  if (!idToken) throw new GoogleProofError("failed", "sin idToken de Google");
  return idToken;
}

/** POST /auth/link-google con el ID token de Google (y, si la hay, la nueva contraseña). */
export async function submitGoogleProof(googleIdToken: string, newPassword?: string): Promise<GoogleProofResult> {
  try {
    const res = await api.post("/auth/link-google", {
      googleIdToken,
      ...(newPassword ? { newPassword } : {}),
    });
    return unwrap(res) as GoogleProofResult;
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
    if (status === 400 && newPassword && /contraseña/i.test(msg || "")) throw new GoogleProofError("badPassword", msg);
    if (status === 403) throw new GoogleProofError("wrongAccount", msg);
    if (status === 409) throw new GoogleProofError("alreadyLinkedElsewhere", msg);
    if (status === 503) throw new GoogleProofError("notConfigured", msg);
    throw new GoogleProofError("failed", msg);
  }
}

export async function verifyWithGoogle(
  auth: Auth,
  email?: string | null,
  newPassword?: string,
): Promise<GoogleProofResult> {
  if (newPassword !== undefined && newPassword.length < MIN_NEW_PASSWORD_LENGTH) {
    throw new GoogleProofError("badPassword");
  }
  const token = await obtainGoogleIdToken(auth, email);
  return submitGoogleProof(token, newPassword);
}

export async function fetchGoogleProofStatus(): Promise<GoogleProofStatus> {
  const res = await api.get("/auth/google-proof");
  return unwrap(res) as GoogleProofStatus;
}
